
import React from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Users } from "lucide-react";
import FeaturedFounderCard from "@/components/founders/FeaturedFounderCard";
import { motion } from "framer-motion";

const Founders = () => {
  // Empty featured founders data - ready for real data
  const featuredFounders: any[] = [];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      className="min-h-screen bg-background"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Hero Section */}
      <motion.div 
        className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent py-16"
        variants={sectionVariants}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Featured <span className="text-primary">Founders</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-muted-foreground mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Meet the young visionaries who are reshaping industries and creating positive impact. 
              Their stories will inspire you to pursue your entrepreneurial dreams.
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Featured Founders Section */}
      <motion.div 
        className="container mx-auto px-4 py-16"
        variants={sectionVariants}
      >
        {featuredFounders.length > 0 ? (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
          >
            {featuredFounders.map((founder, index) => (
              <motion.div
                key={founder.name}
                variants={{
                  hidden: { opacity: 0, y: 50, rotateY: -15 },
                  visible: { 
                    opacity: 1, 
                    y: 0,
                    rotateY: 0,
                    transition: {
                      duration: 0.6,
                      delay: index * 0.15
                    }
                  }
                }}
              >
                <FeaturedFounderCard {...founder} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-semibold mb-4">No Featured Founders Yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Featured founders will appear here once they are added to the platform.
              </p>
            </motion.div>
          </div>
        )}
      </motion.div>

      {/* Feature Application Info */}
      <motion.div 
        className="container mx-auto px-4 py-8"
        variants={sectionVariants}
      >
        <motion.div 
          className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-8 max-w-2xl mx-auto mb-8"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div 
            className="flex items-center justify-center gap-2 mb-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Sparkles className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold">Want to be Featured?</h3>
          </motion.div>
          <motion.p 
            className="text-muted-foreground mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            Join TeenVisor and apply to have your founder story or startup featured in our spotlight. 
            Share your journey, inspire others, and connect with the community.
          </motion.p>
          <motion.div 
            className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <strong>How it works:</strong>
            <ol className="list-decimal list-inside mt-2 space-y-1 text-left">
              <li>Sign up for TeenVisor (it's free!)</li>
              <li>Complete your profile</li>
              <li>Apply for featuring through your dashboard</li>
              <li>Share your story with the community</li>
            </ol>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="bg-muted/50 py-16"
        variants={sectionVariants}
      >
        <div className="container mx-auto px-4 text-center">
          <motion.h2 
            className="text-3xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Become the Next Success Story
          </motion.h2>
          <motion.p 
            className="text-muted-foreground mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Every great founder started with an idea and the courage to pursue it. 
            Join our community and get the support you need to build something amazing.
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button size="lg" className="bg-gradient-to-r from-primary to-primary/80" asChild>
                <Link to="/auth" className="flex items-center">
                  Start Your Journey
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button size="lg" variant="outline" className="flex items-center" asChild>
                <Link to="/auth">
                  <Users className="mr-2 h-4 w-4" />
                  Join Our Community
                </Link>
              </Button>
            </motion.div>
          </motion.div>
          <motion.p 
            className="mt-4 text-sm text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            Already a member? <Link to="/auth" className="text-primary hover:underline">Sign in to apply for featuring</Link>
          </motion.p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Founders;
